import { DeploymentUnitOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import {
	Button,
	DatePicker,
	Form,
	Input,
	Modal,
	Popconfirm,
	Space,
	Table,
	message,
} from 'antd'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import api from '../../api/index.api'
import { ListPageIntro, ListPageShell } from '../../common'
import './ReleaseList.scss'

const RELEASE_DATE_FORMAT = 'YYYY-MM-DD'

export const ReleaseList = () => {
	const [createForm] = Form.useForm()
	const [editForm] = Form.useForm()
	const [isEditModalVisible, setIsEditModalVisible] = useState(false)
	const [editingID, setEditingID] = useState(null)
	const [releaseItems, setReleaseItems] = useState([])
	const [isLoading, setIsLoading] = useState(false)

	const loadReleaseItems = async () => {
		setIsLoading(true)
		try {
			const rs = await api.releaseAdminApi.getReleaseItems()
			const normalizedItems = Array.isArray(rs)
				? rs
				: Array.isArray(rs?.data)
					? rs.data
					: []
			setReleaseItems(normalizedItems)
		} catch (err) {
			message.error(err?.message || 'Could not load release items from backend.')
		} finally {
			setIsLoading(false)
		}
	}

	useEffect(() => {
		loadReleaseItems()
	}, [])

	const handleCreate = async values => {
		try {
			const created = await api.releaseAdminApi.createReleaseItem({
				releaseName: values.releaseName.trim(),
				releaseItems: values.releaseItems.trim(),
				estimatedReleaseDate: values.estimatedReleaseDate.format(RELEASE_DATE_FORMAT),
			})
			setReleaseItems(prev => [created, ...prev])
			createForm.resetFields()
			message.success('Release item created.')
		} catch (err) {
			message.error(err?.message || 'Could not create release item.')
		}
	}

	const handleOpenEdit = record => {
		setEditingID(record.id)
		editForm.setFieldsValue({
			releaseName: record.releaseName,
			releaseItems: record.releaseItems,
			estimatedReleaseDate: moment(record.estimatedReleaseDate, RELEASE_DATE_FORMAT),
		})
		setIsEditModalVisible(true)
	}

	const handleCloseEdit = () => {
		setIsEditModalVisible(false)
		setEditingID(null)
		editForm.resetFields()
	}

	const handleUpdate = async () => {
		try {
			const values = await editForm.validateFields()
			const updated = await api.releaseAdminApi.updateReleaseItem(editingID, {
				releaseName: values.releaseName.trim(),
				releaseItems: values.releaseItems.trim(),
				estimatedReleaseDate:
					values.estimatedReleaseDate.format(RELEASE_DATE_FORMAT),
			})
			setReleaseItems(prev =>
				prev.map(item => (item.id === updated.id ? updated : item))
			)
			handleCloseEdit()
			message.success('Release item updated.')
		} catch (err) {
			if (err?.errorFields) return
			message.error(err?.message || 'Could not update release item.')
		}
	}

	const handleDelete = async id => {
		try {
			await api.releaseAdminApi.deleteReleaseItem(id)
			setReleaseItems(prev => prev.filter(item => item.id !== id))
			message.success('Release item deleted.')
		} catch (err) {
			message.error(err?.message || 'Could not delete release item.')
		}
	}

	const columns = useMemo(
		() => [
			{
				title: 'Release name',
				dataIndex: 'releaseName',
				key: 'releaseName',
				width: 220,
			},
			{
				title: 'Release items',
				dataIndex: 'releaseItems',
				key: 'releaseItems',
				render: value => (
					<span style={{ whiteSpace: 'pre-wrap' }}>{value || ''}</span>
				),
			},
			{
				title: 'Est release date',
				dataIndex: 'estimatedReleaseDate',
				key: 'estimatedReleaseDate',
				width: 160,
			},
			{
				title: 'Actions',
				key: 'actions',
				width: 130,
				render: (_, record) => (
					<Space size="small">
						<Button
							type="text"
							icon={<EditOutlined />}
							onClick={() => handleOpenEdit(record)}
						/>
						<Popconfirm
							title="Delete this release item?"
							okText="Delete"
							cancelText="Cancel"
							onConfirm={() => handleDelete(record.id)}
						>
							<Button type="text" danger icon={<DeleteOutlined />} />
						</Popconfirm>
					</Space>
				),
			},
		],
		[]
	)

	return (
		<ListPageShell>
			<ListPageIntro
				icon={DeploymentUnitOutlined}
				title="Release list"
				subtitle="Track and review all release entries."
			/>
			<div className="release-list">
				<Form
					form={createForm}
					layout="vertical"
					className="release-list__form"
					onFinish={handleCreate}
				>
					<div className="release-list__form-grid">
						<Form.Item
							label="Release name"
							name="releaseName"
							rules={[{ required: true, message: 'Please input release name' }]}
						>
							<Input placeholder="e.g. WordsMine v1.8.0" />
						</Form.Item>
						<Form.Item
							label="Release items"
							name="releaseItems"
							rules={[{ required: true, message: 'Please input release items' }]}
						>
							<Input.TextArea
								placeholder="Describe the release items"
								autoSize={{ minRows: 1, maxRows: 4 }}
							/>
						</Form.Item>
						<Form.Item
							label="Est release date"
							name="estimatedReleaseDate"
							rules={[
								{ required: true, message: 'Please select estimated release date' },
							]}
						>
							<DatePicker
								format={RELEASE_DATE_FORMAT}
								placeholder="Select date"
								style={{ width: '100%' }}
							/>
						</Form.Item>
					</div>
					<Button type="primary" htmlType="submit">
						Add release item
					</Button>
				</Form>
				<Table
					rowKey="id"
					columns={columns}
					dataSource={releaseItems}
					loading={isLoading}
					pagination={{ pageSize: 10 }}
					className="table-common table-common--fluid"
					locale={{ emptyText: 'No release items yet. Add one above.' }}
				/>
			</div>
			<Modal
				title="Edit release item"
				visible={isEditModalVisible}
				onCancel={handleCloseEdit}
				onOk={handleUpdate}
				okText="Update"
			>
				<Form form={editForm} layout="vertical">
					<Form.Item
						label="Release name"
						name="releaseName"
						rules={[{ required: true, message: 'Please input release name' }]}
					>
						<Input />
					</Form.Item>
					<Form.Item
						label="Release items"
						name="releaseItems"
						rules={[{ required: true, message: 'Please input release items' }]}
					>
						<Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
					</Form.Item>
					<Form.Item
						label="Est release date"
						name="estimatedReleaseDate"
						rules={[
							{ required: true, message: 'Please select estimated release date' },
						]}
					>
						<DatePicker format={RELEASE_DATE_FORMAT} style={{ width: '100%' }} />
					</Form.Item>
				</Form>
			</Modal>
		</ListPageShell>
	)
}
